import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { usePlayground } from "../../playground-store";

import ContentArea from "@/components/ContentArea";
import { cn } from "@/lib/utils";
import { getTemplateFormat } from "@/utils/utils";

interface ContentItem {
  type: string;
  text?: string;
}

interface PlaceholderMessage {
  role: string;
  content: string | ContentItem[];
}

interface PlaceholderMessagePreviewProps {
  variableName: string;
}

const extractTextContent = (content: string | ContentItem[]): string => {
  if (typeof content === "string") {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .filter(item => item.type === "text" && item.text)
      .map(item => item.text)
      .join("\n");
  }

  return "";
};

const parseMessagesFromValue = (
  value: string | object | undefined,
): PlaceholderMessage[] | null => {
  if (!value) {
    return null;
  }

  try {
    let parsed: unknown;

    if (typeof value === "string") {
      const trimmedValue = value.trim();

      parsed = JSON.parse(trimmedValue);
    } else {
      parsed = value;
    }

    if (!Array.isArray(parsed)) {
      return null;
    }

    const messages = parsed.filter(
      (item): item is PlaceholderMessage =>
        typeof item === "object" && item !== null && "role" in item && "content" in item,
    );

    return messages.length > 0 ? messages : null;
  } catch {
    return null;
  }
};

const MessagePreviewItem = ({ message }: { message: PlaceholderMessage }) => {
  const playground = usePlayground();
  const textContent = extractTextContent(message.content);

  return (
    <div className="group/message flex min-w-0 flex-col gap-y-1 rounded-lg border border-gray-200 bg-white px-2 py-2">
      <div className="flex items-center gap-x-2">
        <div className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold capitalize text-gray-700">
          {message.role}
        </div>
      </div>
      <div className="min-w-0">
        {textContent ? (
          <ContentArea
            value={textContent}
            readOnly
            size="xs"
            className="pointer-events-none border-0 bg-transparent p-0 shadow-none"
            containerClassName="min-w-0"
            config={{
              format: getTemplateFormat(playground.blueprint.prompt_template),
              disableInteractive: true,
              snippetOverrides: {},
            }}
          />
        ) : (
          <span className="text-xs text-gray-400">(empty)</span>
        )}
      </div>
    </div>
  );
};

const PlaceholderMessagePreview = observer(({ variableName }: PlaceholderMessagePreviewProps) => {
  const playground = usePlayground();
  const [isExpanded, setIsExpanded] = useState(false);

  // Access observables directly for MobX reactivity - don't wrap in useMemo
  const inputVariables = playground.input_variable_sets.map(s => s.variables).flat();
  const matchingVariable = inputVariables.find(v => v.key === variableName);
  const variableValue = matchingVariable?.value;

  const parsedMessages = parseMessagesFromValue(variableValue);

  if (!parsedMessages || parsedMessages.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 transition-all duration-200">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-100"
      >
        {isExpanded ? (
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
        )}
        <span className="text-xs font-medium text-gray-600">
          Preview ({parsedMessages.length} message
          {parsedMessages.length !== 1 ? "s" : ""})
        </span>
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="flex flex-col gap-2 border-t border-gray-200 p-3">
          {parsedMessages.map((message, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <MessagePreviewItem key={idx} message={message} />
          ))}
        </div>
      </div>
    </div>
  );
});

export default PlaceholderMessagePreview;
